import URLJS from "urijs";
import Cookies from "js-cookie";
import * as Axios from "axios";
import { NotifFromGrafferType } from "./notif-from-graffer";
import {
  reportAsyncError,
  reportUnexpectedError,
} from "../../../error-reporter";
import apiClient from "../../apis/tenant-management/tenant-management-client";
import { CloudGovApiError, isAxiosServerError } from "../api-error";
import envConfig from "../../../utils/env-config";
import dayjs from "../../../utils/date-utils";

export const NotifFetchLimit = 20;
const cosmicClient = Axios.default.create();

const LastNotifCheckedAt = "notif_from_graffer_last_checked_at";

// FIXME:環境変数で指定する
const cosmicNotificationsBucketSlug = "graffer-console-notifications";
const cosmicObjectsApiPath = `/cosmicjs/v3/buckets/${cosmicNotificationsBucketSlug}/objects`;
const cosmicNotificationsBucketAPIReadKey =
  "yPPPz7b0yoOxBuoYnqpakLzzk5FCrAjRhzweZrG078hXXPOo04";

class NotifFromGrafferService {
  latest?: Date;

  /**
   * 最新のお知らせを取得する
   */
  async init() {
    if (this.latest) {
      return;
    }
    try {
      const latest = await this.fetchPage(0);
      if (latest.length > 0) {
        const found = latest[0].find((v) => !!v.metadata.should_badge_notice);
        if (found) {
          this.latest = found.published_at;
        }
      }
    } catch (e) {
      reportAsyncError(e);
    }
  }

  /**
   * ユーザーが新着をチェック済みか確認
   */
  shouldNotDisplayBatch() {
    if (!this.latest) {
      return true;
    }
    if (dayjs(this.latest).add(24, "h").isBefore(dayjs())) {
      return true;
    }
    const lastCheckTime = Cookies.get(LastNotifCheckedAt);
    if (!lastCheckTime) {
      return false;
    }
    try {
      return dayjs(this.latest).isBefore(dayjs(parseInt(lastCheckTime, 10)));
    } catch (e) {
      // Cookie の値が意図しない形で保存されている可能性があるので、
      // 念の為 report
      reportUnexpectedError(e);
      return false;
    }
  }

  /**
   * ユーザーの新着チェック時間を更新
   */
  updateLastCheckedAt() {
    Cookies.set(LastNotifCheckedAt, new Date().getTime().toString());
  }

  async fetchPage(page?: number): Promise<[NotifFromGrafferType[], number]> {
    try {
      const { data } = await cosmicClient.get<{
        total: number;
        objects: NotifFromGrafferType[];
      }>(
        this.buildFetchPageQuery({
          skip: page ? (page - 1) * NotifFetchLimit : 0,
        }),
      );
      const total = data.total;
      return [data.objects || [], total];
    } catch (e) {
      //cosmicjsのサーバーエラーの場合、sentryに通知を送らない
      if (!isAxiosServerError(e)) {
        reportUnexpectedError(e);
      }
      return [[], -1];
    }
  }

  async fetchBySlug(slug: string): Promise<NotifFromGrafferType | undefined> {
    const { data } = await cosmicClient.get<{
      objects: NotifFromGrafferType[];
    }>(this.buildFetchDetailQuery(slug));
    if (data.objects.length === 0) {
      return undefined;
    }
    return data.objects[0];
  }

  async fetchBannerNotice(): Promise<NotifFromGrafferType | undefined> {
    try {
      const { data } = await apiClient.fetchBannerNotice();
      if (dayjs(data.displayUntil).isBefore(dayjs())) {
        return;
      }
      return this.fetchBySlug(data.cosmicJSSlug);
    } catch (e) {
      if (CloudGovApiError.is404(e)) {
        return;
      }
      //cosmicjsのサーバーエラーの場合、sentryに通知を送らない
      if (!isAxiosServerError(e)) {
        reportUnexpectedError(e);
      }
      return;
    }
  }

  private buildFetchPageQuery({
    limit = NotifFetchLimit,
    skip = 0,
  }: {
    limit?: number;
    skip?: number;
  }) {
    const url = new URLJS(cosmicObjectsApiPath);
    // set default params
    url.setQuery("pretty", "true");
    url.setQuery(
      "query",
      JSON.stringify({
        type: this.getNotifBucketSlug(),
      }),
    );
    url.setQuery("read_key", cosmicNotificationsBucketAPIReadKey);
    url.setQuery("status", "published");
    url.setQuery("sort", "-created_at");
    url.setQuery("props", "slug,title,content,metadata,published_at");
    url.setQuery("limit", limit.toString());
    url.setQuery("skip", skip.toString());

    return url.toString();
  }

  private buildFetchDetailQuery(slug: string) {
    const url = new URLJS(
      // NOTE:objectsのAPIはslugで取得することができないため、objectsのAPIを使用
      // レスポンスはarrayなので要注意
      // https://www.cosmicjs.com/docs/api/objects#get-a-single-object-by-slug
      cosmicObjectsApiPath,
    );
    // set default params
    url.setQuery("pretty", "true");
    url.setQuery(
      "query",
      JSON.stringify({
        type: this.getNotifBucketSlug(),
        slug: slug,
      }),
    );
    url.setQuery("read_key", cosmicNotificationsBucketAPIReadKey);
    url.setQuery("props", "slug,title,content,metadata,published_at");
    url.setQuery("status", "published");

    return url.toString();
  }

  private getNotifBucketSlug() {
    return envConfig.matchEnvValue({
      local: "test-notices",
      dev: "test-notices",
      staging: "test-notices",
      staging_sandbox: "test-notices",
      production: "notices",
      production_sandbox: "notices",
    });
  }
}

export const GrafferNotifService = new NotifFromGrafferService();
